import { useEffect, useState } from "react";
import { List, Typography, Spin, Empty, Card, Skeleton } from "antd";
import MentorshipAPI from "../../services/MentorshipAPI";
import type { Mentor } from "../../models/Mentor";
import MentorCard from "./MentorCard";
import ErrorBoundary from "../../components/ErrorBoundary";
const { Title, Text } = Typography;

const LoadingMentorCard = () => (
    <Card>
        <Skeleton.Avatar active size={128} shape="circle" style={{ marginBottom: 16 }} />
        <Skeleton active paragraph={{ rows: 3 }} />
    </Card>
);

const LoadingMentorsList = () => {
    // Create an array of 8 loading cards
    const loadingCards = Array(8).fill({});
    
    return (
        <div className="container mx-auto p-6">
            <Skeleton.Input style={{ width: 300, height: 40 }} active className="mb-8" />
            <ErrorBoundary fallback={<div>Error loading mentors</div>}>
                <List
                    grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 4 }}
                    dataSource={loadingCards}
                renderItem={() => (
                    <List.Item>
                        <LoadingMentorCard />
                    </List.Item>
                )}
            />
            </ErrorBoundary>
        </div>
    );
};

export default function MentorsList() {
    const [mentors, setMentors] = useState<Mentor[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchMentors = async () => {
            try {
                const response = await MentorshipAPI.listMentors();
                setMentors(response.data.results);
            } catch (err: any) {
                setError(err.message || "Failed to load mentors");
            } finally {
                setLoading(false);
            }
        };

        fetchMentors();
    }, []);

    if (loading) {
        return <LoadingMentorsList />;
    }

    if (error) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Text type="danger">{error}</Text>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6">
            <Title level={2} className="mb-8 pt-6">Available Mentors</Title>
            
            {mentors.length === 0 ? (
                <Empty description="No mentors available" />
            ) : (
                <List
                    grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 4 }}
                    dataSource={mentors}
                    renderItem={(mentor) => (
                        <List.Item>
                            <MentorCard mentor={mentor} />
                        </List.Item>
                    )}
                />
            )}
        </div>
    );
} 